/**
 * WhySeeingThis — small "?" icon that opens a tooltip explaining why
 * an AI-generated card is showing what it's showing.
 *
 * Phase 84.23.2 (Activation Suite v2 — trust tooltips).
 *
 * Rationale: users hitting the Financial Briefing / Needs Your
 * Attention / Insights cards often ask "how did it know that?".
 * Without a clear source, they either doubt the output ("is this
 * made up?") or over-trust it ("why is 2120 telling me to save
 * more?"). One tooltip turns opaque AI into transparent AI:
 *
 *   • Emergency Fund card → "Because your Emergency Fund goal
 *     is at 100 % (₹120,000 of ₹120,000)."
 *   • Home Loan EMI nudge → "Because you have a recurring
 *     debit of ₹32,000 to HDFC Home Loan due Jul 28."
 *
 * Every consumer just passes a `reason` string — no state, no
 * effects, no logic. Renders as a Radix Popover so it works on
 * touch + keyboard.
 */
import React from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

export default function WhySeeingThis({ reason, testid, className, label }) {
  if (!reason) return null;
  const triggerCls = className
    || 'inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70 hover:text-charcoal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Why am I seeing this?"
          data-testid={testid || 'why-seeing-this'}
          className={triggerCls}
        >
          <HelpCircle className="h-3 w-3" aria-hidden="true" />
          {label || 'Why?'}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="max-w-xs text-xs leading-relaxed text-charcoal p-3"
        data-testid="why-seeing-this-content"
      >
        <p className="font-medium text-charcoal mb-1">Why you&rsquo;re seeing this</p>
        <p className="text-muted-foreground">{reason}</p>
      </PopoverContent>
    </Popover>
  );
}
